/**
 * useCustomerFilters - 고객 목록 필터 상태 관리 훅
 *
 * URL params 동기화, KPI 세그먼트 필터, 정렬 옵션 통합 관리
 */

import { useState, useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

export type CustomerSortOrder =
  | "createdAt,desc"
  | "createdAt,asc"
  | "name,asc"
  | "name,desc"
  | "lastInteractionAt,desc";

export type CustomerSegment =
  | "all"
  | "starred"
  | "newThisMonth"
  | "dormant"
  | null;

export type CustomerViewMode = "list" | "grid";

export interface CustomerFilterState {
  searchQuery: string;
  starred: boolean | undefined;
  sortOrder: CustomerSortOrder;
  page: number;
  pageSize: number;
  segment: CustomerSegment;
  viewMode: CustomerViewMode;
  // CRM 확장 필드 (향후 활성화)
  tags?: string[];
  tier?: string;
}

const SORT_OPTIONS: { value: CustomerSortOrder; label: string }[] = [
  { value: "createdAt,desc", label: "최근 등록순" },
  { value: "createdAt,asc", label: "오래된 순" },
  { value: "name,asc", label: "이름순 (ㄱ→ㅎ)" },
  { value: "name,desc", label: "이름순 (ㅎ→ㄱ)" },
  { value: "lastInteractionAt,desc", label: "최근 상담순" },
];

const PAGE_SIZE_OPTIONS = [15, 30, 50] as const;

function isValidSort(value: string | null): value is CustomerSortOrder {
  return SORT_OPTIONS.some((opt) => opt.value === value);
}

function isValidPageSize(
  value: number,
): value is (typeof PAGE_SIZE_OPTIONS)[number] {
  return (PAGE_SIZE_OPTIONS as readonly number[]).includes(value);
}

export function useCustomerFilters() {
  const [searchParams, setSearchParams] = useSearchParams();

  // URL에서 초기값 복원
  const urlQ = searchParams.get("q") || "";
  const urlStarred = searchParams.get("starred");
  const urlSort = searchParams.get("sort");
  const urlPage = searchParams.get("page");
  const urlSize = searchParams.get("size");
  const urlSegment = searchParams.get("segment");
  const urlView = searchParams.get("view");

  const [filters, setFiltersInternal] = useState<CustomerFilterState>({
    searchQuery: urlQ,
    starred: urlStarred === "true" ? true : undefined,
    sortOrder: isValidSort(urlSort) ? urlSort : "createdAt,desc",
    page: urlPage ? parseInt(urlPage, 10) : 0,
    pageSize:
      urlSize && isValidPageSize(parseInt(urlSize, 10))
        ? (parseInt(urlSize, 10) as CustomerFilterState["pageSize"])
        : 15,
    segment: (urlSegment as CustomerSegment) || null,
    viewMode: (urlView === "grid" ? "grid" : "list") as CustomerViewMode,
  });

  // URL 동기화
  useEffect(() => {
    const params = new URLSearchParams();

    if (filters.searchQuery) params.set("q", filters.searchQuery);
    if (filters.starred) params.set("starred", "true");
    if (filters.sortOrder !== "createdAt,desc")
      params.set("sort", filters.sortOrder);
    if (filters.page > 0) params.set("page", filters.page.toString());
    if (filters.pageSize !== 15)
      params.set("size", filters.pageSize.toString());
    if (filters.segment && filters.segment !== "all")
      params.set("segment", filters.segment);
    if (filters.viewMode !== "list") params.set("view", filters.viewMode);

    setSearchParams(params, { replace: true });
  }, [filters, setSearchParams]);

  const setSearch = useCallback((query: string) => {
    setFiltersInternal((prev) => ({ ...prev, searchQuery: query, page: 0 }));
  }, []);

  const setStarred = useCallback((starred: boolean | undefined) => {
    setFiltersInternal((prev) => ({ ...prev, starred, page: 0 }));
  }, []);

  const setSortOrder = useCallback((sortOrder: CustomerSortOrder) => {
    setFiltersInternal((prev) => ({ ...prev, sortOrder, page: 0 }));
  }, []);

  const setPage = useCallback((page: number) => {
    setFiltersInternal((prev) => ({ ...prev, page }));
  }, []);

  const setPageSize = useCallback((pageSize: number) => {
    setFiltersInternal((prev) => ({ ...prev, pageSize, page: 0 }));
  }, []);

  const setSegment = useCallback((segment: CustomerSegment) => {
    setFiltersInternal((prev) => {
      // 세그먼트에 따라 starred 필터 자동 적용
      const starred = segment === "starred" ? true : undefined;
      return { ...prev, segment, starred, page: 0 };
    });
  }, []);

  const setViewMode = useCallback((viewMode: CustomerViewMode) => {
    setFiltersInternal((prev) => ({ ...prev, viewMode }));
  }, []);

  const resetFilters = useCallback(() => {
    setFiltersInternal({
      searchQuery: "",
      starred: undefined,
      sortOrder: "createdAt,desc",
      page: 0,
      pageSize: 15,
      segment: null,
      viewMode: "list",
    });
  }, []);

  // API 파라미터 변환
  const apiParams = useMemo(
    () => ({
      q: filters.searchQuery || undefined,
      starred: filters.starred || undefined,
      page: filters.page,
      size: filters.pageSize,
      sort: filters.sortOrder,
      segment:
        filters.segment && filters.segment !== "all"
          ? filters.segment
          : undefined,
    }),
    [filters],
  );

  return {
    filters,
    apiParams,
    setSearch,
    setStarred,
    setSortOrder,
    setPage,
    setPageSize,
    setSegment,
    setViewMode,
    resetFilters,
    sortOptions: SORT_OPTIONS,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
  };
}
